import { PayoutCalculationParams, PayoutResult, PayoutDetail } from '../types/payout';

export function calculatePayouts({
  thresholds,
  calculationBasis,
  policyLimit,
  weatherData,
  modelSelect,
  parameterSelect
}: PayoutCalculationParams): PayoutResult {
  const sortedThresholds = [...thresholds].sort((a, b) => a.trigger - b.trigger);
  const yearCount = 5; // Used for average annual calculation
  let totalPayout = 0;
  let triggeredDays = 0;
  const details: PayoutDetail[] = [];

  if (modelSelect === 'drought') {
    // Process drought data
    Object.entries(weatherData).forEach(([state, data]) => {
      const stateData = data.all;
      const dates = Object.keys(stateData).sort();

      if (calculationBasis === 'occurrence') {
        // Process each date individually
        dates.forEach(date => {
          const droughtData = stateData[date];
          const totalDroughtPercentage = droughtData.total_drought_percentage;

          // Find applicable threshold
          const triggered = sortedThresholds.filter(th => totalDroughtPercentage > th.trigger);
          if (triggered.length > 0) {
            const maxTrigger = triggered[triggered.length - 1];
            const dayPayout = (maxTrigger.payout / 100) * policyLimit;
            totalPayout += dayPayout;
            triggeredDays++;

            details.push({
              location: state,
              day: droughtData.date,
              value: totalDroughtPercentage,
              payout: dayPayout
            });
          }
        });
      } else {
        // Aggregate basis - use average drought percentage over period
        const avgDroughtPercentage = dates.reduce((sum, date) => {
          return sum + stateData[date].total_drought_percentage;
        }, 0) / dates.length;

        const triggered = sortedThresholds.filter(th => avgDroughtPercentage > th.trigger);
        if (triggered.length > 0) {
          const maxTrigger = triggered[triggered.length - 1];
          const periodPayout = (maxTrigger.payout / 100) * policyLimit;
          totalPayout += periodPayout;
          triggeredDays++;

          details.push({
            location: state,
            day: `${dates[0]} to ${dates[dates.length - 1]}`,
            value: avgDroughtPercentage,
            payout: periodPayout
          });
        }
      }
    });
  } else if (modelSelect === 'xweather') {
    // Process storm reports
    const locationReports = weatherData.reduce((acc: Record<string, any[]>, report) => {
      const location = report.locationName || 'Unknown Location';
      if (!acc[location]) acc[location] = [];
      acc[location].push(report);
      return acc;
    }, {});

    Object.entries(locationReports).forEach(([location, reports]) => {
      if (calculationBasis === 'occurrence') {
        reports.forEach(report => {
          const value = report.report?.detail?.hailIN ? parseFloat(report.report.detail.hailIN) : 0;
          if (value > 0) {
            const triggered = sortedThresholds.filter(th => value > th.trigger);
            if (triggered.length > 0) {
              const maxTrigger = triggered[triggered.length - 1];
              const dayPayout = (maxTrigger.payout / 100) * policyLimit;
              totalPayout += dayPayout;
              triggeredDays++;
              details.push({
                location,
                day: report.report?.dateTimeISO?.split('T')[0] || 'Unknown',
                value,
                payout: dayPayout
              });
            }
          }
        });
      } else {
        const totalValue = reports.reduce((sum, report) => {
          const value = report.report?.detail?.hailIN ? parseFloat(report.report.detail.hailIN) : 0;
          return sum + (value > 0 ? value : 0);
        }, 0);

        const triggered = sortedThresholds.filter(th => totalValue > th.trigger);
        if (triggered.length > 0) {
          const maxTrigger = triggered[triggered.length - 1];
          const aggregatePayout = (maxTrigger.payout / 100) * policyLimit;
          totalPayout += aggregatePayout;
          triggeredDays++;

          if (reports.length > 0) {
            details.push({
              location,
              day: `${reports[0].report?.dateTimeISO?.split('T')[0]} to ${reports[reports.length - 1].report?.dateTimeISO?.split('T')[0]}`,
              value: totalValue,
              payout: aggregatePayout
            });
          }
        }
      }
    });
  } else {
    // Process OpenMeteo data
    weatherData.forEach(point => {
      if (!Array.isArray(point.times) || !Array.isArray(point.values)) return;

      const location = `${point.lat.toFixed(4)}, ${point.lon.toFixed(4)}`;

      if (calculationBasis === 'occurrence') {
        point.values.forEach((value, index) => {
          if (value === null || value === undefined) return;

          const triggered = sortedThresholds.filter(th => value > th.trigger);
          if (triggered.length > 0) {
            const maxTrigger = triggered[triggered.length - 1];
            const dayPayout = (maxTrigger.payout / 100) * policyLimit;
            totalPayout += dayPayout;
            triggeredDays++;
            details.push({
              location,
              day: point.times[index].split('T')[0],
              value,
              payout: dayPayout
            });
          }
        });
      } else {
        const validValues = point.values.filter(v => v !== null && v !== undefined);
        if (validValues.length === 0) return;

        const totalValue = validValues.reduce((sum, value) => sum + value, 0);
        const avgValue = totalValue / validValues.length;

        const triggered = sortedThresholds.filter(th => avgValue > th.trigger);
        if (triggered.length > 0) {
          const maxTrigger = triggered[triggered.length - 1];
          const aggregatePayout = (maxTrigger.payout / 100) * policyLimit;
          totalPayout += aggregatePayout;
          triggeredDays++;

          details.push({
            location,
            day: `${point.times[0].split('T')[0]} to ${point.times[point.times.length - 1].split('T')[0]}`,
            value: avgValue,
            payout: aggregatePayout
          });
        }
      }
    });
  }

  const avgAnnualLoss = totalPayout / yearCount;

  return {
    policyLimit,
    mode: calculationBasis === 'occurrence' ? 'Occurrence' : 'Aggregate',
    triggeredDays,
    totalPayout,
    avgAnnualLoss,
    details
  };
}